import React, { useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, MapPin, Users, Search as SearchIcon, X, ChevronDown, Check } from "lucide-react";
import Loading from "../../Loading";
import SLink from "../../components/SLink";
import useDBO from "../../hooks/useDBO";
import Error from "../../components/Error";
import { CenterContext, ClassesContext } from "../../../../Context";
import VND from "../../components/VND";
import HtClass from "../../components/HtClass";
import Lang from "../../components/Lang";

/**
 * PaymentFrequency — Displays customers due for tuition renewal by month and location.
 * Uses URL search params (?date=YYYY-MM-01&location=id) for shareable state.
 * Card-based layout with location summary, frequency breakdown, and student table.
 *
 * @component
 * @author Forge, opencode + glm-5
 * @date 2026-04-15
 * @version 2.0.0
 */
export default function PaymentFrequency() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const locationParam = searchParams.get("location");

  const defaultMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-01`;
  const nextBillDate = dateParam || defaultMonth;
  const location = locationParam || "";

  const { locations } = useContext(CenterContext);
  const students = useDBO("/payment-frequency/" + nextBillDate);

  const [classFilter, setClassFilter] = React.useState("");
  const [nameSearch, setNameSearch] = React.useState("");

  const setDate = (d) => {
    const params = new URLSearchParams(searchParams);
    if (d) params.set("date", d);
    else params.delete("date");
    setSearchParams(params, { replace: true });
  };

  const setLocation = (id) => {
    const params = new URLSearchParams(searchParams);
    if (id) params.set("location", id);
    else params.delete("location");
    setSearchParams(params, { replace: true });
  };

  const uniqueMonths = useMemo(() => {
    if (!students || students.loading || students.error) return [];
    return [
      ...new Set(
        students
          .map((s) => s.nextBillDate?.substring(0, 7))
          .filter((m) => m)
      ),
    ].sort();
  }, [students]);

  const filteredStudents = useMemo(() => {
    if (!students || students.loading || students.error) return [];
    const currentMonth = nextBillDate.substring(0, 7);
    return students.filter((s) => {
      const matchesDate = s.nextBillDate?.substring(0, 7) === currentMonth;
      const matchesLoc = !location || s.classes?.some((c) => c.managedBy === location);
      const matchesClass = !classFilter || s.classes?.map((c) => c._id).includes(classFilter);
      const matchesName = !nameSearch || s.name?.toLowerCase().includes(nameSearch.toLowerCase());
      return matchesDate && matchesLoc && matchesClass && matchesName;
    });
  }, [students, location, classFilter, nameSearch, nextBillDate]);

  const classCounts = useMemo(() => {
    const counts = {};
    for (let s of filteredStudents) {
      for (let c of s.classes?.filter((c) => (!location || c.managedBy === location)) || []) {
        counts[c._id] = (counts[c._id] || 0) + 1;
      }
    }
    return counts;
  }, [filteredStudents, location]);

  const locationStats = useMemo(() => {
    if (!students || students.loading || students.error) return [];
    const currentMonth = nextBillDate.substring(0, 7);
    return locations.map((l) => {
      const locStudents = students.filter(
        (s) =>
          s.nextBillDate?.substring(0, 7) === currentMonth &&
          getLocation(s) === l._id
      );
      const byFreq = {};
      for (const f of [1, 2, 3]) {
        byFreq[f] = locStudents.filter((s) => s.paymentFrequency === f).length;
      }
      return { id: l._id, name: l.name, total: locStudents.length, byFreq };
    });
  }, [students, locations, nextBillDate]);

  const allStats = useMemo(() => {
    if (!students || students.loading || students.error) return { total: 0, byFreq: {} };
    const currentMonth = nextBillDate.substring(0, 7);
    const all = students.filter((s) => s.nextBillDate?.substring(0, 7) === currentMonth);
    const byFreq = {};
    for (const f of [1, 2, 3]) {
      byFreq[f] = all.filter((s) => s.paymentFrequency === f).length;
    }
    return { total: all.length, byFreq };
  }, [students, nextBillDate]);

  if (students.loading) return <Loading />;
  if (students.error) return <Error />;


  console.log(students.get)
  return (
    <div className="p-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">
          <Lang w="paymentFrequency" />
        </h1>
      </div>

      {/* Month selector */}
      <div className="flex items-center gap-2 mb-4">
        <CalendarDays size={16} className="text-slate-400 flex-shrink-0" />
        <div className="flex gap-1.5 flex-wrap">
          {uniqueMonths.map((month) => {
            const isActive = nextBillDate.startsWith(month);
            const count = students.filter((s) => s.nextBillDate?.substring(0, 7) === month).length;
            return (
              <button
                key={month}
                onClick={() => setDate(month + "-01")}
                className={`
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-[0.85rem] font-semibold
                  transition-all duration-150 border
                  ${
                    isActive
                      ? "bg-teal-600 text-white border-teal-600 shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-[#f8fafc]"
                  }
                `}
              >
                <span>
                  {new Date(month + "-01").getMonth() + 1}/
                  {new Date(month + "-01").getFullYear().toString().slice(-2)}
                </span>
                <span className={`text-[0.7rem] ${isActive ? "text-white/80" : "text-slate-400"}`}>
                  ({count})
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Location cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-4">
        {/* All card */}
        <button
          onClick={() => setLocation("")}
          className={`
            relative text-left p-4 rounded-[12px] border-2 transition-all duration-150 cursor-pointer
            ${
              !location
                ? "border-teal-600 bg-teal-50 shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
                : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
            }
          `}
        >
          {!location && (
            <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-teal-600 flex items-center justify-center">
              <Check size={9} className="text-white" strokeWidth={3} />
            </div>
          )}
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className={!location ? "text-teal-600" : "text-slate-400"} />
            <span className={`text-[0.85rem] font-bold ${!location ? "text-teal-600" : "text-slate-700"}`}>
              <Lang w="all" />
            </span>
          </div>
          <div className={`text-2xl font-bold ${!location ? "text-teal-600" : "text-slate-800"}`}>
            {allStats.total}
          </div>
          <div className="flex gap-1.5 mt-2">
            {[1, 2, 3].map((f) => (
              <span
                key={f}
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[0.65rem] font-semibold ${
                  !location
                    ? "bg-teal-600/10 text-teal-600"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {f}m:{allStats.byFreq[f] || 0}
              </span>
            ))}
          </div>
        </button>

        {/* Per-location cards */}
        {locationStats.map((loc) => {
          const isActive = location === loc.id;
          return (
            <button
              key={loc.id}
              onClick={() => setLocation(loc.id)}
              className={`
                relative text-left p-4 rounded-[12px] border-2 transition-all duration-150 cursor-pointer
                ${
                  isActive
                    ? "border-teal-600 bg-teal-50 shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                }
              `}
            >
              {isActive && (
                <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-teal-600 flex items-center justify-center">
                  <Check size={9} className="text-white" strokeWidth={3} />
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={14} className={isActive ? "text-teal-600" : "text-slate-400"} />
                <span className={`text-[0.85rem] font-bold truncate ${isActive ? "text-teal-600" : "text-slate-700"}`}>
                  {loc.name}
                </span>
              </div>
              <div className={`text-2xl font-bold ${isActive ? "text-teal-600" : "text-slate-800"}`}>
                {loc.total}
              </div>
              <div className="flex gap-1.5 mt-2">
                {[1, 2, 3].map((f) => (
                  <span
                    key={f}
                    className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[0.65rem] font-semibold ${
                      isActive
                        ? "bg-teal-600/10 text-teal-600"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {f}m:{loc.byFreq[f] || 0}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* Filters bar: name search + class filter */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {/* Name search */}
        <div className="relative flex-1 min-w-[200px] max-w-[320px] group">
          <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none" style={{ paddingLeft: "12px" }}>
            <SearchIcon size={15} className="text-slate-400 group-focus-within:text-teal-600 transition-colors duration-150" />
          </div>
          <input
            type="text"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            placeholder="Search by name..."
            className="w-full bg-[#f8fafc] border border-slate-200 rounded-[10px] text-slate-800 text-[0.85rem] placeholder-slate-400 focus:outline-none focus:border-teal-600 focus:ring-[3px] focus:ring-[rgba(0,0,0,0.04)] transition-all duration-150 hover:bg-white hover:border-slate-300"
            style={{ paddingLeft: "34px", paddingRight: nameSearch ? "32px" : "12px", height: "36px" }}
          />
          {nameSearch && (
            <button
              onClick={() => setNameSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-5 h-5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-all duration-150"
              aria-label="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* Class filter dropdown */}
        <ClassFilterSelect
          classCounts={classCounts}
          value={classFilter}
          setValue={setClassFilter}
        />
      </div>

      {/* Student table */}
      <div className="bg-white rounded-[12px] border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#f8fafc] border-b border-slate-200">
              <th className="text-left text-[0.75rem] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">
                <Lang w="name" />
              </th>
              <th className="text-left text-[0.75rem] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">
                <Lang w="class" />
              </th>
              <th className="text-right text-[0.75rem] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">
                <Lang w="amount" />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-12 text-slate-400 text-sm">
                  <Users size={32} strokeWidth={1.5} className="mx-auto mb-2 text-slate-300" />
                  No students found
                </td>
              </tr>
            ) : (
              filteredStudents.map((s, idx) => (
                <tr
                  key={s._id}
                  className={`
                    border-b border-slate-100 transition-colors duration-100
                    ${idx % 2 === 0 ? "bg-white" : "bg-[#fafbfc]"}
                    hover:bg-[#f1f5f9]
                  `}
                >
                  <td className="px-4 py-2.5">
                    <SLink id={s._id} showPhone />
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {s.classes?.map((c) => (
                        <HtClass key={c._id} htClass={c._id} />
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-[0.85rem] font-semibold text-slate-700">
                    <VND value={s.bills?.reduce((a, b) => a + b.amount, 0) || 0} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * ClassFilterSelect — Custom dropdown for filtering by class.
 * Uses flat-premium design with Tailwind teal theming.
 *
 * @component
 * @param {Object} classCounts - Map of classId -> count
 * @param {string} value - Current selected class ID
 * @param {Function} setValue - Setter for the selected class
 * @author Forge, opencode + glm-5
 * @date 2026-04-15
 * @version 1.0.0
 */
function ClassFilterSelect({ classCounts, value, setValue }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const wrapperRef = React.useRef(null);
  const searchRef = React.useRef(null);
  const { findClass } = useContext(ClassesContext);

  React.useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    const timer = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const classEntries = Object.entries(classCounts);
  const selectedLabel = value ? (<HtClass htClass={value} link={false} />) : null;

  const filteredEntries = search
    ? classEntries.filter(([id]) => {
        const cl = findClass(id);
        return cl?.name?.toLowerCase().includes(search.toLowerCase());
      })
    : classEntries;

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-slate-200 bg-[#f8fafc] text-[0.85rem] text-slate-700 hover:bg-white hover:border-slate-300 transition-all duration-150"
      >
        <span className="font-medium">
          {value ? selectedLabel : "All classes"}
        </span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] z-50 overflow-hidden min-w-[200px]"
          style={{ animation: "classDropIn 150ms cubic-bezier(0.4, 0, 0.2, 1)" }}
        >
          <div
            onClick={() => { setValue(""); setIsOpen(false); }}
            className={`px-3.5 py-2.5 cursor-pointer transition-colors duration-100 border-l-[3px] flex items-center justify-between gap-3 ${
              !value
                ? "bg-teal-50 border-l-teal-600"
                : "border-l-transparent hover:bg-[#f8fafc] hover:border-l-slate-300"
            }`}
          >
            <span className={`text-[0.85rem] ${!value ? "font-bold text-slate-900" : "font-semibold text-slate-600"}`}>
              All classes
            </span>
            {!value && (
              <div className="flex-shrink-0 w-4 h-4 rounded-full bg-teal-600 flex items-center justify-center text-white">
                <Check size={9} strokeWidth={3} />
              </div>
            )}
          </div>

          {classEntries.length > 5 && (
            <div className="px-2 py-1.5 border-b border-slate-100">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search classes..."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg text-slate-800 text-[0.8rem] placeholder-slate-400 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-[rgba(0,0,0,0.03)] h-7 px-2.5 transition-all duration-150"
              />
            </div>
          )}

          <div className="max-h-[260px] overflow-y-auto">
            {filteredEntries.length === 0 ? (
              <div className="py-4 text-center text-[0.8rem] text-slate-400 italic">
                No classes found
              </div>
            ) : (
              filteredEntries.map(([id, count]) => {
                const isSelected = value === id;
                return (
                  <div
                    key={id}
                    onClick={() => { setValue(id); setIsOpen(false); }}
                    className={`px-3.5 py-2.5 cursor-pointer transition-colors duration-100 border-l-[3px] flex items-center justify-between gap-3 ${
                      isSelected
                        ? "bg-teal-50 border-l-teal-600"
                        : "border-l-transparent hover:bg-[#f8fafc] hover:border-l-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <HtClass htClass={id} link={false} />
                    </div>
                    <span className="text-[0.7rem] text-slate-400 font-medium flex-shrink-0">({count})</span>
                    {isSelected && (
                      <div className="flex-shrink-0 w-4 h-4 rounded-full bg-teal-600 flex items-center justify-center text-white">
                        <Check size={9} strokeWidth={3} />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <style>{`
            @keyframes classDropIn {
              from { opacity: 0; transform: translateY(-6px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}

/**
 * Get primary location for a student based on their active classes.
 * @param {Object} student - Student object with classes array
 * @returns {string|undefined} Location ID of the student's primary active class
 */
function getLocation(student) {
  if (!student.classes || student.classes.length === 0) return undefined;
  const active = student.classes.filter((c) => c.status === "Active");
  if (active.length === 0) return undefined;
  return active[0].managedBy || undefined;
}